import React, { createContext, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { MapInteractionCSS } from 'react-map-interaction';
import { SearchOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useCaseApplication } from '../../cases/shared/CaseApplicationContext';
import { Step } from 'utils/enumerations';
import { observer } from 'mobx-react';

const StyledPanZoomElement = styled.div`
  position: relative;
  height: 100%;
  & .content-container {
    position: absolute;
    left: 0;
    top: 0;
  }
  &&& div div div {
    transition: all 0.2s;
  }
`;

const StyledZoomButtonRow = styled.div`
  position: absolute;
  left: 0px;
  top: 0px;
  width: 100%;
  background-color: transparent;
  display: flex;
  justify-content: center;
  z-index: 10;
`;

const StyledZoomButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  z-index: 11;
  background-color: white;
  border-radius: 3px;
  border: 1px solid #948f8f;
  position: relative;
  top: 6px;

  &:focus-within {
    border-width: 2px;
  }
`;

const StyledSearchSymbolContainer = styled.div`
  font-size: 1.5em;
  margin-left: 5px;
  margin-right: 5px;
`;

const StyledZoomButton = styled(Button)`
  & {
    color: #3795ed;
  }

  &:focus,
  &:hover {
    color: #1454a3;
  }
`;

export const PanZoomContext = createContext();

export const PanZoomElement = observer(({ children }) => {
  const [t] = useTranslation();
  const application = useCaseApplication();
  const pageRef = React.useRef();
  const zoomRef = React.useRef();
  const [loaded, setLoaded] = React.useState(false);
  const [zoomDisabled, setZoomDisabled] = React.useState(true);
  const [scrollDisabled, setScrollDisabled] = React.useState(false);
  const [panDisabled, setPanDisabled] = React.useState(false);
  const [bounds, setBounds] = React.useState({
    xMin: 0,
    xMax: 0,
    yMin: 0,
    yMax: 0
  });
  const margin = 100;

  const [isDragging, setIsDragging] = React.useState(false);
  const [value, setValue] = React.useState({ scale: 0.85, translation: { x: 0, y: 0 } });
  const [width, setWidth] = React.useState(0);
  const [height, setHeight] = React.useState(0);

  const onLoad = React.useCallback(() => {
    const bounds = {
      xMin: -(pageRef.current.offsetWidth / 2),
      xMax: zoomRef.current.offsetWidth - pageRef.current.offsetWidth / 2,
      yMin: -(pageRef.current.offsetHeight / 2),
      yMax: zoomRef.current.offsetHeight - pageRef.current.offsetHeight / 2
    };

    setBounds(bounds);
    setWidth(zoomRef.current.offsetWidth);
    setHeight(zoomRef.current.offsetHeight);

    //Fix mouse cursor style.
    zoomRef.current.childNodes[0].childNodes[0].style.cursor = 'default';
    setLoaded(true);
  }, []);

  useEffect(() => {
    onLoad();
  }, [onLoad]);

  const onMouseDown = useCallback(() => {
    setIsDragging(isDragging => (isDragging ? false : isDragging));
    document.activeElement.blur();
  }, []);

  const onMouseUp = useCallback(() => {
    setPanDisabled(false);
  }, []);

  const onResize = useCallback(() => {
    const newWidth = zoomRef.current?.offsetWidth ?? 0;
    const newHeight = zoomRef.current?.offsetHeight ?? 0;

    setWidth(newWidth);
    setHeight(newHeight);
    setValue(oldValue => {
      const translation = {
        x: oldValue.translation.x + (newWidth - width) / 2,
        y: oldValue.translation.y + (newHeight - height) / 2
      };
      return { ...oldValue, translation };
    });
  }, [height, width]);

  const onChange = useCallback(newValue => {
    const bounds = {
      xMin: -pageRef.current.offsetWidth * newValue.scale + margin,
      xMax: zoomRef.current.offsetWidth - margin,
      yMin: -pageRef.current.offsetHeight * newValue.scale + margin,
      yMax: zoomRef.current.offsetHeight - margin
    };
    setBounds(bounds);
    setIsDragging(true);
    setValue(newValue);
  }, []);

  const disableScroll = useCallback(value => {
    setScrollDisabled(value);
  }, []);

  const disablePan = useCallback(value => {
    setPanDisabled(value);
  }, []);

  const reScale = useCallback(newScale => {
    const bounds = {
      xMin: -pageRef.current.offsetWidth * newScale + margin,
      xMax: zoomRef.current.offsetWidth - margin,
      yMin: -pageRef.current.offsetHeight * newScale + margin,
      yMax: zoomRef.current.offsetHeight - margin
    };
    setBounds(bounds);

    setValue(oldValue => {
      const translation = {
        x:
          zoomRef.current.offsetWidth / 2 -
          ((zoomRef.current.offsetWidth / 2 - oldValue.translation.x) * newScale) / oldValue.scale,
        y:
          zoomRef.current.offsetHeight / 2 -
          ((zoomRef.current.offsetHeight / 2 - oldValue.translation.y) * newScale) / oldValue.scale
      };
      return { scale: newScale, translation };
    });
  }, []);

  const onButtonZoomIn = useCallback(() => {
    let newScale = value.scale * 1.25;
    newScale = newScale > 3 ? 3 : newScale;
    reScale(newScale);
  }, [value.scale, reScale]);

  const onButtonZoomOut = useCallback(() => {
    let newScale = value.scale * 0.8;
    newScale = newScale < 0.3 ? 0.3 : newScale;
    reScale(newScale);
  }, [value.scale, reScale]);

  const enableZoom = useCallback(
    event => {
      if (event.keyCode === 17 && zoomDisabled === true && scrollDisabled === false) {
        setZoomDisabled(false);
      } else if (event.keyCode === 17 && scrollDisabled === true) {
        setZoomDisabled(true);
      }
    },
    [scrollDisabled, zoomDisabled]
  );

  const disableZoom = useCallback(
    event => {
      if (event.keyCode === 17 && zoomDisabled === false) {
        setZoomDisabled(true);
      }
    },
    [zoomDisabled]
  );

  const onWheel = useCallback(
    event => {
      if (!scrollDisabled && pageRef.current && zoomRef.current) {
        setValue(oldValue => {
          const translation = { x: oldValue.translation.x, y: oldValue.translation.y - event.deltaY };
          if (
            translation.y > -pageRef.current.offsetHeight * oldValue.scale + margin &&
            translation.y < zoomRef.current.offsetHeight - margin
          ) {
            return { ...oldValue, translation };
          } else {
            return oldValue;
          }
        });
      }
    },
    [scrollDisabled]
  );

  const scrollToActivePanel = useCallback(() => {
    const element = pageRef.current?.querySelector('.selectedPanel');
    if (element) {
      setValue(oldValue => {
        const visibleHeight = pageRef.current.parentElement.parentElement.parentElement.parentElement.offsetHeight;
        const offsetTop = element.offsetTop + (element.offsetParent ? element.offsetParent.offsetTop : 0);
        const translation = oldValue.translation;
        if (-oldValue.translation.y > offsetTop * oldValue.scale) {
          translation.y = (-offsetTop + element.offsetHeight * 0.5) * oldValue.scale;
        } else if (-oldValue.translation.y + visibleHeight < (offsetTop + element.offsetHeight) * oldValue.scale) {
          translation.y = (visibleHeight / oldValue.scale - offsetTop - element.offsetHeight * 2) * oldValue.scale;
        }
        return { ...oldValue, translation };
      });
    }
  }, []);

  const scrollToMap = useCallback(() => {
    const element = pageRef.current?.querySelector('.preview-map');
    if (element) {
      setValue(oldValue => {
        const visibleHeight = pageRef.current.parentElement.parentElement.parentElement.parentElement.offsetHeight;
        const offsetTop = element.offsetTop + (element.offsetParent ? element.offsetParent.offsetTop : 0);
        const translation = oldValue.translation;
        translation.y = (visibleHeight / oldValue.scale - offsetTop - element.offsetHeight * 2) * oldValue.scale;
        return { ...oldValue, translation };
      });
    }
  }, []);

  useEffect(() => {
    scrollToActivePanel();
  }, [application.currentPanel, scrollToActivePanel]);

  useEffect(() => {
    const prevStep = application.currentStep;

    return () => {
      //TODO: Improve this when screenshot functionality changes.
      if (prevStep === Step.Map) {
        setTimeout(() => {
          scrollToMap();
          scrollToActivePanel();
        }, 100);
      }
    };
  }, [application.currentStep, scrollToMap, scrollToActivePanel]);

  useEffect(() => {
    const zoomHandler = zoomRef.current;
    if (zoomHandler) {
      zoomHandler.addEventListener('wheel', onWheel);
    }
    window.addEventListener('resize', onResize);
    window.addEventListener('keydown', enableZoom);
    window.addEventListener('keyup', disableZoom);
    return () => {
      if (zoomHandler) {
        zoomHandler.removeEventListener('wheel', onWheel);
      }
      window.removeEventListener('resize', onResize);
      window.removeEventListener('keydown', enableZoom);
      window.removeEventListener('keyup', disableZoom);
    };
  }, [disableZoom, enableZoom, onResize, onWheel]);

  return (
    <StyledPanZoomElement ref={zoomRef}>
      <StyledZoomButtonRow>
        <StyledZoomButtonContainer className="zoomButtonContainer">
          <Tooltip title={t('preview:zoomOut')}>
            <StyledZoomButton
              icon={<MinusOutlined />}
              disabled={value.scale <= 0.3}
              onClick={onButtonZoomOut}
              type="link"
            />
          </Tooltip>
          <StyledSearchSymbolContainer>
            <SearchOutlined style={{ color: '#948f8f' }} />
          </StyledSearchSymbolContainer>
          <Tooltip title={t('preview:zoomIn')}>
            <StyledZoomButton
              icon={<PlusOutlined />}
              disabled={value.scale >= 3}
              onClick={onButtonZoomIn}
              type="link"
            />
          </Tooltip>
        </StyledZoomButtonContainer>
      </StyledZoomButtonRow>
      <MapInteractionCSS
        disableZoom={zoomDisabled}
        disablePan={panDisabled}
        translationBounds={bounds}
        value={value}
        minScale={0.3}
        maxScale={3}
        onChange={onChange}
      >
        <div
          role="presentation"
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onTouchEnd={onMouseUp}
          className="content-container"
          data-testid="panZoomElementDiv"
        >
          <PanZoomContext.Provider
            value={{
              isDragging: isDragging,
              pageRef: pageRef,
              scale: value.scale,
              disableScroll: disableScroll,
              disablePan: disablePan,
              onLoad: !loaded ? onLoad : undefined
            }}
          >
            {children}
          </PanZoomContext.Provider>
        </div>
      </MapInteractionCSS>
    </StyledPanZoomElement>
  );
});
